import { getEnvironment } from "../getEnvironment.ts";
import { FlagState } from "./FeatureFlagManager.ts";
import { isCLA, isPLA } from "./hostnameCheckers.ts";

export const initialState = {
  enableHiddenFlags: {
    name: "Hidden Flags",
    description:
      "Enable the display of hidden flags in the Feature Flag Manager, allowing access to dev features.",
    defaultValue: false,
    hidden: true,
  },
  enableFilterChips: {
    name: "Filter Chips",
    description:
      "Enable display of filter chips in the Chat Box, indicating the current filters applied.",
    defaultValue: true,
    hidden: true,
  },
  enableFakeApi: {
    name: "Fake API",
    description:
      "Enable a Fake implementation of the API for testing purposes.",
    defaultValue: false,
    hidden: true,
  },
  enableRemoveFiles: {
    name: "Remove Files",
    description:
      "Enable the ability to remove files using the Reference sidebar.",
    defaultValue: true,
    hidden: true,
  },
  enableFilters: {
    name: "Filters",
    description: "Enable the button to invoke the filters modal.",
    defaultValue: true,
    hidden: true,
  },
  enableAppliedFilters: {
    name: "Applied Filters",
    description:
      "Enable the visualization of the filters applied when each message was sent.",
    defaultValue: true,
    hidden: true,
  },
  enableRelatedQuestions: {
    name: "Related Questions",
    description:
      "Enable a list of related questions at the bottom of responses.",
    defaultValue: true,
    hidden: true,
  },
  enableGlobalNav: {
    name: "Global Navigation",
    description:
      "Replace the top navigation with a global navigation, including a menu to other Ipsos tools.",
    defaultValue: false,
  },

  enableRegenerateSummary: {
    name: "Regenerate Summary",
    description: "Enable the button in the chat box to regenerate the summary",
    defaultValue: true,
    hidden: true,
  },
  enableLabs: {
    name: "Labs UI",
    description:
      "Enable the Labs button in the nav bar, allowing access to experimental features.",
    defaultValue: false,
    hidden: true,
  },
  enableResearchTypes: {
    name: "Research Types",
    description:
      "Enable the ability to select the type of research during research creation.",
    defaultValue: true,
    hidden: true,
  },
  enableResearchTypeDescriptions: {
    name: "Research Type Descriptions",
    description:
      "Enable the display of descriptions for each research type in the workflow wizard.",
    defaultValue: true,
    hidden: true,
  },
  enableNumberedCitations: {
    name: "Numbered Citations",
    description:
      "Enable the use of numbered citations in the chat messages, replacing the link icon style.",
    defaultValue: false,
  },
  enableSplitCitations: {
    name: "Split Citations",
    description:
      "Enable the display of split citation markers in the chat messages, allowing multiple references to be displayed individually instead of grouped together.",
    defaultValue: false,
  },
  enablePasswordlessLogin: {
    name: "Passwordless Login",
    description:
      "Enable the passwordless login feature, allowing users to log in using a magic link instead of a password.",
    defaultValue: getEnvironment() === "beta",
  },
  enableFirebaseEmulator: {
    name: "Firebase Emulator",
    description:
      "Enable the use of Firebase Emulator for local development and testing.",
    defaultValue: false,
    hidden: true,
  },
  enableTermsAndConditions: {
    name: "Terms and Conditions",
    description:
      "Enable the Terms and Conditions modal that users must accept before using the application.",
    defaultValue: getEnvironment() === "beta",
  },
  enableIngestionFolderLink: {
    name: "Ingestion Folder Link",
    description:
      "Enable the link to the Ingestion folder in the Knowledge Source selection dropdown.",
    defaultValue: true,
  },
  enableThemeToggle: {
    name: "Theme Toggle",
    description:
      "Enable the theme toggle in the top navigation, allowing users to switch between light and dark themes.",
    defaultValue: true,
    hidden: true,
  },
  enableDynamicFilters: {
    name: "Dynamic Filters",
    description:
      "Loads filter options dynamically from the backend, allowing for more flexible and customizable filtering options.",
    defaultValue: true,
    hidden: true,
  },
  enableHistorySidebarRedesign: {
    name: "History Sidebar Redesign",
    description:
      "Enable the redesigned History Sidebar with improved UI and UX.",
    defaultValue: true,
  },
  enableClaMode: {
    name: "CLA mode",
    description:
      "Customizes the application for the Concept Learning Agent and Product Testing use cases.",
    defaultValue: isCLA() || isPLA(),
  },
  enableDeepResearchAgent: {
    name: "Replace DSA with DRA",
    description:
      "Replaces the Data Science Agent with the Deep Research Agent.",
    defaultValue: true,
    hidden: true,
  },
  enableCloudRun: {
    name: "Agents on Cloud Run",
    description:
      "Replace agent calls with Cloud Run (instead of Agent Engine).",
    defaultValue: true,
    hidden: true,
  },
  enableLimitedHeightExecutionDetails: {
    name: "Limited Height Execution Details",
    description:
      "Limit the maximum height of the Execution Details component with a scroll bar.",
    defaultValue: true,
    hidden: true,
  },
  enableRetryLastQuery: {
    name: "Retry Last Query",
    description:
      "Enable the option to retry the last query from the user. Currently used in CLA mode only.",
    defaultValue: true,
    hidden: true,
  },
  enableProductTestingBusinessDomain: {
    name: "Business domain is Product Testing",
    description: "Enable the Product Testing business domain.",
    defaultValue: isPLA(),
  },
  enableChatFirstPage: {
    name: "Chat First",
    description: "Enable a chat-first page for Insights Agent Deep Research.",
    defaultValue: true,
    hidden: false,
  },
  enableNewTermsAndConditionsFlow: {
    name: "New Terms and Conditions Flow",
    description:
      "Enable the new Terms and Conditions flow with updated API endpoints and logic.",
    defaultValue: false,
    hidden: false,
  },
  enableFloatingCitationWidget: {
    name: "Floating Citation Widget",
    description:
      "Enable the new Floating Citation Widget for displaying citations in chat messages.",
    defaultValue: true,
    hidden: false,
  },
} as const satisfies FlagState;
